import { useCallback, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useToastController } from '@tamagui/toast'
import { Text, XStack, YStack } from 'tamagui'
import { Button } from '@components/Button'
import { CoverImage } from '@components/CoverImage'
import { DirectPurchaseModal } from '@components/Home/CustomProductList/DirectPurchase/DirectPurchaseModal'
import { withPurchaseOrigin } from '@components/Home/CustomProductList/DirectPurchase/purchaseOrigin'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { ICustomProductListItem } from '@dtos/MembersConfig'
import { GET_COURSE_NEXT_LEARNING } from '@services/api/graphql/queries/getCourseNextLearning'
import { useGetProductUrl } from '@services/api/graphql/productUrl'
import { openWebBrowser } from '@utils/openWebBrowser'

export const UNLOCK_POLL_INTERVAL_MS = 3000
const UNLOCK_POLL_ATTEMPTS = 10

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Card permanente na sidebar de aulas, como o "Seu próximo aprendizado está aqui!" da Hotmart:
// oferta o primeiro produto da fila do curso que o aluno ainda não tem. Comprar abre o mesmo painel
// das seções da home (1 clique com cartão salvo ou Pix), carregado só no toque.
export function NextLearningCard() {
  const { t } = useTranslation('DirectPurchase')
  const toast = useToastController()
  const { courseId } = useCourseRouteParams()
  const { isPreview } = useAdminPreview()
  const [purchaseItem, setPurchaseItem] =
    useState<ICustomProductListItem | null>(null)
  const { data, refetch } = useQuery(GET_COURSE_NEXT_LEARNING, {
    variables: { courseId: String(courseId) },
    skip: !courseId || isPreview,
  })
  const { getProductUrl } = useGetProductUrl({ itemId: '', productListId: '' })

  const list = data?.membersConfig?.customProductLists?.[0]
  const item: ICustomProductListItem | undefined = list?.items?.find(
    (i: ICustomProductListItem) =>
      !i.activeForUser && i.kind === 'direct_purchase' && !!i.offerId,
  )

  const handleGoToCheckout = useCallback(
    async (target: ICustomProductListItem) => {
      setPurchaseItem(null)
      const { data: urlData } = await getProductUrl({
        variables: { itemId: target.id, productListId: list?.id },
      })
      if (urlData?.productUrl) {
        openWebBrowser(
          withPurchaseOrigin(urlData.productUrl, 'next_learning'),
          true,
        )
      }
    },
    [getProductUrl, list?.id],
  )

  // A matrícula nasce assíncrona no core: relê a fila até o produto chegar liberado, e o card passa
  // sozinho para o próximo da fila.
  const handlePurchased = useCallback(
    async (purchased: ICustomProductListItem) => {
      setPurchaseItem(null)
      for (let attempt = 0; attempt < UNLOCK_POLL_ATTEMPTS; attempt++) {
        const { data: fresh } = await refetch().catch(() => ({ data: null }))
        const unlocked =
          fresh?.membersConfig?.customProductLists?.[0]?.items?.find(
            (i: ICustomProductListItem) => i.id === purchased.id,
          )?.activeForUser
        if (unlocked) {
          toast.show(t('toast.title'), { message: t('toast.unlocked') })
          return
        }
        await wait(UNLOCK_POLL_INTERVAL_MS)
      }
      toast.show(t('toast.title'), { message: t('toast.unlocking') })
    },
    [refetch, t, toast],
  )

  if (!item) return null

  return (
    <YStack
      gap="$size.spacing-xxs"
      padding="$spacing-inset-xs"
      borderTopWidth={1}
      borderColor="$neutral-200"
      testID="next-learning-card"
    >
      <Text color="$neutral-800" ff="$mulishBold" fontSize="$font-size-xxs">
        {t('title')}
      </Text>
      <XStack gap="$size.spacing-xxs" alignItems="center">
        <CoverImage uri={item.logo} width={96} height={54} borderRadius={4} />
        <Text
          flex={1}
          color="$neutral-800"
          ff="$mulishRegular"
          fontSize="$font-size-xxs"
          numberOfLines={2}
        >
          {item.title}
        </Text>
      </XStack>
      <Button
        variant="cta"
        size="small"
        onPress={() => setPurchaseItem(item)}
        aria-label={t('buyNow')}
      >
        {t('buyNow')}
      </Button>
      <DirectPurchaseModal
        item={purchaseItem}
        entry="next_learning"
        onClose={() => setPurchaseItem(null)}
        onGoToCheckout={handleGoToCheckout}
        onPurchased={handlePurchased}
      />
    </YStack>
  )
}
